import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Cog, ClipboardList, X } from "lucide-react";
import { listDiagnostics, listMaintenanceOrders } from "../api";
import type { DiagnosticCode, MaintenanceOrder, Vehicle } from "../types";

export function MaintenancePanel({
  open,
  onClose,
  vehicles,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
}) {
  const [orders, setOrders] = useState<MaintenanceOrder[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiagnosticCode[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      Promise.all([listMaintenanceOrders(), listDiagnostics()])
        .then(([orderRows, diagnosticRows]) => {
          setOrders(orderRows);
          setDiagnostics(diagnosticRows);
        })
        .catch((reason) => setError(reason instanceof Error ? reason.message : "No fue posible cargar diagnóstico"));
    };
    refresh();
    window.addEventListener("gsf:maintenance-updated", refresh);
    return () => window.removeEventListener("gsf:maintenance-updated", refresh);
  }, [open]);

  const activeCodes = useMemo(
    () => diagnostics.filter((diagnostic) => diagnostic.active),
    [diagnostics],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel maintenance-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">J1939 · DM1 / DM2</span>
            <h2 id="maintenance-title">Diagnóstico y mantenimiento</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar mantenimiento">
            <X size={18} />
          </button>
        </header>

        <section className="admin-panel__summary">
          <span><AlertTriangle size={17} /><b>{activeCodes.length}</b> fallas activas</span>
          <span><ClipboardList size={17} /><b>{orders.filter((order) => order.status !== "resolved").length}</b> órdenes abiertas</span>
          <span><CheckCircle2 size={17} /><b>{orders.filter((order) => order.status === "resolved").length}</b> resueltas</span>
        </section>

        <div className="admin-panel__body maintenance-panel__body">
          <section>
            <div className="admin-section-title">
              <span className="eyebrow">BANDEJA DE TRABAJO</span>
              <b>{orders.length} registros</b>
            </div>
            <div className="maintenance-list">
              {orders.map((order) => {
                const vehicle = vehicles.find((item) => item.id === order.vehicle_id);
                return (
                  <article className={`maintenance-item maintenance-item--${order.priority}`} key={order.id}>
                    <Cog size={16} />
                    <span>
                      <small>{vehicle?.unit_code || `UNIDAD ${order.vehicle_id}`} · {order.status}</small>
                      <strong>{order.title}</strong>
                      <em>{order.description}</em>
                    </span>
                    <b>OT-{String(order.id).padStart(5, "0")}</b>
                  </article>
                );
              })}
              {!orders.length && <p className="maintenance-empty">SIN ÓRDENES REGISTRADAS</p>}
            </div>
          </section>

          <section>
            <div className="admin-section-title">
              <span className="eyebrow">CÓDIGOS RECIBIDOS</span>
              <b>{activeCodes.length} activos</b>
            </div>
            <div className="diagnostic-list">
              {diagnostics.map((diagnostic) => {
                const vehicle = vehicles.find((item) => item.id === diagnostic.vehicle_id);
                return (
                  <article key={diagnostic.id}>
                    <i className={`status-dot ${diagnostic.active ? "is-critical" : "is-online"}`} />
                    <span>
                      <strong>SPN {diagnostic.spn} · FMI {diagnostic.fmi}</strong>
                      <small>{vehicle?.unit_code || diagnostic.vehicle_id} · {diagnostic.protocol} · OC {diagnostic.occurrence_count}</small>
                      <em>{diagnostic.metadata_json.fmi_label || "Consultar documentación OEM"}</em>
                    </span>
                    <code>{diagnostic.raw_code}</code>
                  </article>
                );
              })}
              {!diagnostics.length && <p className="maintenance-empty">SIN CÓDIGOS J1939</p>}
            </div>
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
